// Core types for courage-coop — evidence-weighted map for cooperatives

export type Coop = {
  id: string
  slug: string
  name: string
  description: string
  evidence_threshold: number // 0-10. Only debts with computed evidence >= this appear in this co-op's map view
  created_at: string
}

export type Membership = {
  coop_id: string
  role: 'member' | 'steward'
}

export type SocialDebt = {
  id: string
  coop_id: string
  owner_hash: string
  created_at: string

  // The four required captures (structured, consent-gated)
  social_debt_item: string          // short description of the "small ask" / coordination debt. Can be lightly descriptive.
  avoidance_intensity: number       // 0-10. How much it was avoided / courage required to surface
  repair_willingness: number        // 1-7. Willingness of parties (or self) to make repair if needed
  closure_action: string            // what actually resolved it, or a concrete action that would close it. The "repair grammar".

  // Evidence weighting inputs (what decides if this enters the map)
  evidence_types: string[]          // e.g. ['self_experienced','steward_observed','repeated_in_group']
  evidence_notes?: string           // optional short corroborating context (never private therapy note)

  // Derived / computed at write + on attest
  evidence_weight: number           // 0-10+ composite. Drives visibility + prominence.

  // Consents (granular, never assumed)
  map_consent: 0 | 1                // show in the cooperative cognitive map
  capsule_consent: 0 | 1            // may be included in community memory capsules
  nuance_consent: 0 | 1             // open to public nuance contributions

  deleted_at?: string
}

export type Nuance = {
  id: string
  debt_id: string
  coop_id: string
  author_hash?: string              // pseudonymous within co-op; null/omitted for fully public
  text: string                      // the nuance itself — structural, observable, non-shaming
  created_at: string
  // AI or human generated flag for the nuance machine
  source: 'human' | 'machine'
}

export type Attestation = {
  id: string
  debt_id: string
  coop_id: string
  owner_hash: string
  strength: number                  // 1-3 how strongly they corroborate
  note?: string
  created_at: string
}

export type Capsule = {
  id: string
  coop_id: string
  title: string
  sealed_at: string
  sealed_by_hash: string
  r2_key: string                    // points to full capsule bundle in R2
  included_debt_count: number
  included_nuance_count: number
  orientation_text?: string         // short AI+human generated framing for the capsule
  version: number
}

export type LiveMapState = {
  coop_id: string
  points: MapPoint[]
  total_mapped: number
  high_evidence_count: number
  last_updated: string
  experiment_variant?: string
}

export type MapPoint = {
  id: string
  x: number                         // normalized 0-1 — Avoidance (high right = high courage cost)
  y: number                         // normalized 0-1 — Repair Orientation (high = strong willingness + concrete closure)
  debt_item: string                 // short label (truncated)
  avoidance: number
  repair: number
  evidence: number
  has_closure: boolean
  is_personal: boolean
}

export type OwnerState = {
  secret: string
  hash: string
  active_coop_ids: string[]
}

export type ExperimentVariant = {
  key: string
  value: string
  description: string
}

// Evidence weighting function (pure, testable)
export function computeEvidenceWeight(
  base: number,                     // from self report / type
  attestations: number,             // count of corroborations
  hasConcreteClosure: boolean,
  repeated: boolean
): number {
  let w = base
  w += Math.min(3.5, attestations * 1.1)
  if (hasConcreteClosure) w += 1.6
  if (repeated) w += 0.9
  return Math.max(0, Math.min(12, Math.round(w * 10) / 10))
}

// Map position grammar (the cognitive topography for co-ops)
export function positionOnMap(avoidance: number, repair: number): { x: number; y: number } {
  // X = courage cost / avoidance (right = rarely surfaced, high unseen labor)
  // Y = repair orientation (up = high willingness + concrete, successful or promising closure path)
  const x = Math.max(0, Math.min(1, avoidance / 10))
  const y = Math.max(0, Math.min(1, (repair - 1) / 6))
  return { x, y }
}

export const EVIDENCE_TYPES = [
  'self_experienced',
  'peer_mentioned',
  'steward_observed',
  'successful_closure',
  'repeated_in_group',
  'documented_precedent',
] as const

export type EvidenceType = typeof EVIDENCE_TYPES[number]

export const DEBT_CATEGORIES = [
  'coordination',
  'resource',
  'process_clarity',
  'emotional_labor_light',
  'intro_or_connection',
  'feedback_or_tension',
  'capacity_request',
] as const

export type DebtCategory = typeof DEBT_CATEGORIES[number]
